import { ModalAction } from '@overlayqa/shared/ui';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  PROJECT_COLORS,
  PROJECT_ICONS,
  getProjectIconAndColor,
  linkedFrameNodeId,
} from '@overlayqa/shared';
import type { ParsedFigmaUrl, ProjectFigmaFile, ProjectRole } from '@overlayqa/shared';
import { useAddProjectFigmaFile, useUpdateProject } from '../../../hooks/useProjects';
import { SelectDropdown } from '../../shared/SelectDropdown';
import { ProjectFigmaFilesSection } from '../ProjectFigmaFilesSection';
import { ThumbnailField } from './ThumbnailField';
import { log } from '@/lib/logger';

interface GeneralTabProps {
  project: {
    id: string;
    name: string;
    primaryDomain?: string | null;
    thumbnailUrl?: string | null;
    icon?: string | null;
    iconColor?: string | null;
    role?: ProjectRole | null;
    figmaFiles?: ProjectFigmaFile[];
  };
  onClose: () => void;
}

/** Moved verbatim from the retired ProjectEditModal: the URL field stores a domain. */
function extractDomain(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      return new URL(trimmed).hostname.replace(/^www\./, '');
    }
    return trimmed.replace(/^www\./, '').split('/')[0];
  } catch {
    return trimmed.split('/')[0] || null;
  }
}

/**
 * The General tab of the Project Settings modal: thumbnail with Retake, name,
 * URL, Figma files, and Icon and Color.
 *
 * Replaces the retired ProjectEditModal body (2026-09-17). Two things it
 * deliberately does NOT carry over:
 *  - the "Review page URL" field (Emily, 2026-09-17: "remove review page url
 *    option from modal"). Capture Issues opens its automatic page instead:
 *    the newest issue's page, else the scan URL, else this URL.
 *  - the single "Figma File" field, which wrote the one-slot figmaFileKey.
 *    The file list below is the project's source of truth since 2026-09-10.
 *
 * Save covers the form fields. Figma files, like Retake, save on their own
 * action, with one exception: a valid link pasted but never linked is linked by
 * Save, so reaching for the modal's one primary button does not drop it.
 */
export function GeneralTab({ project, onClose }: GeneralTabProps) {
  const updateProject = useUpdateProject();
  const addFigmaFile = useAddProjectFigmaFile();
  const resolved = getProjectIconAndColor(project);

  const [name, setName] = useState(project.name);
  const [urlInput, setUrlInput] = useState(project.primaryDomain ?? '');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [icon, setIcon] = useState(project.icon ?? resolved.icon.key);
  const [iconColor, setIconColor] = useState(project.iconColor ?? resolved.color.key);
  const [pendingFigma, setPendingFigma] = useState<ParsedFigmaUrl | null>(null);
  const [saving, setSaving] = useState(false);

  // Mirrors requireProjectWrite (refuses `!role || role === 'viewer'`), which
  // every Figma file write calls.
  const canWriteProject = project.role != null && project.role !== 'viewer';
  const colorHex = PROJECT_COLORS.find((c) => c.key === iconColor)?.hex ?? resolved.color.hex;

  const handleSave = async () => {
    if (!name.trim()) return;

    const trimmedUrl = urlInput.trim();
    const urlChanged = trimmedUrl !== (project.primaryDomain ?? '').trim();
    const domain = trimmedUrl ? extractDomain(trimmedUrl) : null;
    if (urlChanged && trimmedUrl && (!domain || domain.length > 253)) {
      setUrlError('Enter a valid URL or domain');
      return;
    }

    setSaving(true);
    try {
      await updateProject.mutateAsync({
        id: project.id,
        // PATCH validates every supplied domain (server/routes/projects.ts:
        // ownAppPrimaryDomainRefusal). An untouched legacy URL must not block
        // a name/icon edit; an explicit URL edit or clear still goes through it.
        data: { name: name.trim(), icon, iconColor, ...(urlChanged ? { primaryDomain: domain } : {}) },
      });
    } catch (err) {
      log.error('[GeneralTab] Failed to update project', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to save project. Please try again.');
      setSaving(false);
      return;
    }

    if (pendingFigma?.isValid) {
      try {
        await addFigmaFile.mutateAsync({
          projectId: project.id,
          data: {
            fileKey: pendingFigma.fileKey,
            nodeId: linkedFrameNodeId(pendingFigma.nodeId),
            name: pendingFigma.fileName ?? null,
          },
        });
      } catch (err) {
        log.error('[GeneralTab] Failed to link the pasted Figma file on Save', { projectId: project.id, error: err instanceof Error ? err.message : String(err) });
        toast.error(err instanceof Error && err.message ? err.message : 'Failed to link the Figma file. Please try again.');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    onClose();
  };

  return (
    <form
      className="px-6 pt-4 pb-5"
      // The Figma paste field is <input type="url">. Without noValidate a
      // non-URL typed there makes the browser block Save for the WHOLE form
      // with its own bubble. This tab validates its fields itself, and a bad
      // paste is simply not linked.
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void handleSave();
      }}
    >
      <ThumbnailField project={project} />

      <fieldset className="mb-5">
        <label htmlFor="settingsProjectName" className="field-label">Project Name</label>
        <input
          id="settingsProjectName"
          // Mirrors updateProjectSchema.name max(100) — packages/server/src/routes/projects.ts
          maxLength={100}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
          className="input input-lg w-full"
        />
      </fieldset>

      <fieldset className="mb-5">
        <label htmlFor="settingsProjectUrl" className="field-label">URL</label>
        <input
          id="settingsProjectUrl"
          type="text"
          value={urlInput}
          onChange={(e) => {
            setUrlInput(e.target.value);
            setUrlError(null);
          }}
          placeholder="https://example.com"
          className={`input input-lg w-full${urlError ? ' input-error' : ''}`}
          aria-invalid={!!urlError}
          aria-describedby={urlError ? 'settingsProjectUrl-error' : undefined}
        />
        {urlError ? (
          <p id="settingsProjectUrl-error" className="mt-1 text-xs text-red-600" role="alert">{urlError}</p>
        ) : (
          <p className="field-hint mt-1">The primary domain for this project</p>
        )}
      </fieldset>

      <fieldset className="mb-5">
        <legend className="field-label">
          Figma files <span className="text-gray-400 font-normal">(optional)</span>
        </legend>
        <ProjectFigmaFilesSection
          projectId={project.id}
          files={project.figmaFiles ?? []}
          canWrite={canWriteProject}
          onPendingChange={setPendingFigma}
        />
      </fieldset>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <label htmlFor="settingsProjectIcon" className="field-label">Icon</label>
          <SelectDropdown
            id="settingsProjectIcon"
            aria-label="Icon"
            size="lg"
            className="w-full"
            value={icon}
            onChange={setIcon}
            options={PROJECT_ICONS.map((option) => ({
              value: option.key,
              label: option.label,
              leading: (
                <svg className="w-4 h-4" style={{ color: colorHex }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d={option.path} />
                </svg>
              ),
            }))}
          />
        </div>
        <div>
          <label htmlFor="settingsProjectColor" className="field-label">Color</label>
          <SelectDropdown
            id="settingsProjectColor"
            aria-label="Color"
            size="lg"
            className="w-full"
            value={iconColor}
            onChange={setIconColor}
            options={PROJECT_COLORS.map((option) => ({
              value: option.key,
              label: option.label,
              leading: <span className="block w-3 h-3 rounded-full" style={{ backgroundColor: option.hex }} />,
            }))}
          />
        </div>
      </div>

      {/* Pinned to the bottom of the modal's scroller, not floating at the end
         of the form. Measured 2026-09-17 at 1440x900, the commonest laptop
         height: this tab is taller than the shell's max-h, so Save's bottom
         landed at 927px, 27px under the fold (127px at vh=800), and the modal
         opened with its primary action out of sight. The shell already scrolls
         (max-h + overflow-y-auto on the tab wrapper); what was missing is that
         the action row rode inside that scroller. The other three tabs are
         short enough that sticky changes nothing for them. */}
      <div className="sticky bottom-0 -mx-6 -mb-5 mt-2 flex gap-3 justify-end border-t border-gray-100 bg-white px-6 py-3">
        <button type="button" className="btn-secondary" onClick={onClose}><ModalAction kind="cancel">
          Cancel
        </ModalAction></button>
        <button type="submit" className="btn-primary" disabled={!name.trim() || saving}><ModalAction kind="save">
          {saving ? 'Saving...' : 'Save'}
        </ModalAction></button>
      </div>
    </form>
  );
}
